// What the screen does with chat entries, in one place.

const displayName = (name) => name.charAt(0).toUpperCase() + name.slice(1);

const joinNames = (names) =>
  names.length < 2
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * Convert an entry from GET /chat_history to the shape sessions hold.
 *
 * @param {object} entry - The server's entry.
 * @param {number} idx - Its place in the list, if it has no index of its own.
 * @returns {object} The entry for a session's chatHistory.
 */
export const historyEntryFromServer = (entry, idx) => ({
  user: entry.user ?? '',
  assistant: entry.assistant ?? '',
  index: typeof entry.index === 'number' ? entry.index : idx,
  raw_index: entry.raw_index,
  files: entry.files || undefined,
  timestamp: entry.timestamp || undefined,
  preliminary_answer: entry.preliminary_answer || undefined,
  evaluation_report: entry.evaluation_report || undefined,
  agents_left_out: entry.agents_left_out || undefined,
  evaluation_agents_left_out: entry.evaluation_agents_left_out || undefined,
});

/**
 * Say which agents a run left out, and why.
 *
 * Written here rather than by the model, whose instructions keep agent names
 * out of what it writes. A wrong key otherwise goes unnoticed for as long as
 * another agent works.
 *
 * @param {Object<string, string>|null|undefined} leftOut - Agent name to the
 *   reason it was left out, by type and status.
 * @returns {string} The note, or an empty string if no agent was left out.
 */
export const agentsLeftOutNote = (leftOut) => {
  const entries = Object.entries(leftOut || {});
  if (entries.length === 0) return '';
  if (entries.length === 1) {
    const [[name, reason]] = entries;
    const shown = displayName(name);
    return (
      `${shown} didn't answer (${reason}), so it's left out of this. ` +
      `If ${shown} uses your own key, check that config in Settings.`
    );
  }
  const names = joinNames(entries.map(([name]) => displayName(name)));
  const reasons = entries
    .map(([name, reason]) => `${displayName(name)}: ${reason}`)
    .join('; ');
  return (
    `${names} didn't answer (${reasons}), so they're left out of this. ` +
    'If they use your own keys, check those configs in Settings.'
  );
};
